import { useContext, useEffect, useMemo, useRef, useState } from "react"
import ReactMarkdown from "react-markdown"
import CircularProgress from "@mui/material/CircularProgress"
import { useReactToPrint } from "react-to-print"
import LiteratureReviewLayout from "../../Components/LiteratureReviewLayout/LiteratureReviewLayout"
import { useLiteratureReviewReport } from "../../Context/LiteratureReviewReportContext"
import { generateReport, getSynthesisStatus, simplifyLitReviewReport, translateLitReviewReport, generateResearchGap, getResearchGapStatus } from "../../Utils/functions"
import { exportLitReviewPpt } from "../../Utils/exportLitReviewPpt"
import ReportTypeSelector from "../../Components/ReportTypeSelector/ReportTypeSelector"
import LanguageSelector from "../../Components/LanguageSelector/LanguageSelector"
import FollowUpQuestions from "../../Components/FollowUpQuestions/FollowUpQuestions"
import ResearchGap from "../../Components/ResearchGap/ResearchGap"
import { usePoll } from "../../hooks/usePoll"
import "./ReportGeneration.css"
import { useLiteratureReview } from "../../Context/LiteratureReviewContext"
import { NavigationContext } from "../../Context/NavigationContext"

const STUDY_ROWS_PREVIEW_COUNT = 3

const formatReportDate = () => {
    const d = new Date()
    const months = ["JANUARY", "FEBRUARY", "MARCH", "APRIL", "MAY", "JUNE", "JULY", "AUGUST", "SEPTEMBER", "OCTOBER", "NOVEMBER", "DECEMBER"]
    return `${months[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`
}

function formatStudyCitation(article) {
    const rawAuthors = article?.authors
    const authorsList = Array.isArray(rawAuthors) ? rawAuthors : (rawAuthors ? [rawAuthors] : [])
    const firstAuthor = authorsList[0] ?? ""

    let initial = ""
    let lastName = ""

    if (firstAuthor.includes(",")) {
        // "Smith, John" format
        const parts = firstAuthor.split(",")
        lastName = parts[0].trim()
        const firstName = (parts[1] ?? "").trim()
        initial = firstName ? firstName[0].toUpperCase() + "." : ""
    } else {
        // "John Smith" or "Smith J" format
        const parts = firstAuthor.trim().split(/\s+/)
        if (parts.length >= 2) {
            const last = parts[parts.length - 1]
            const first = parts[0]
            // Detect "Smith J" pattern (last part is a single letter)
            if (last.length === 1 || (last.length === 2 && last[1] === ".")) {
                lastName = parts.slice(0, -1).join(" ")
                initial = last[0].toUpperCase() + "."
            } else {
                // "John Smith" pattern
                lastName = last
                initial = first[0].toUpperCase() + "."
            }
        } else {
            lastName = firstAuthor
        }
    }

    const year = article?.publicationDate ? String(article.publicationDate).match(/\d{4}/)?.[0] ?? "" : ""
    const suffix = authorsList.length > 1 ? " et al." : ""
    const name = [initial, lastName].filter(Boolean).join(" ")
    const title = typeof article?.title === "string" ? article.title.trim() : ""
    return name && year ? `${name}${suffix}, ${year}` : name || year || title || "Unknown"
}

const scrollToReportReference = (refId) => {
    const el = document.getElementById(refId)
    if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" })
    }
}

const reportMarkdownComponents = {
    a: ({ href, children, ...props }) => {
        const refId = typeof href === "string" && href.startsWith("#paper-") ? href.slice(1) : null
        if (!refId) {
            return <a href={href} {...props}>{children}</a>
        }
        const childText = Array.isArray(children)
            ? children.map((c) => (typeof c === "string" || typeof c === "number" ? c : "")).join("")
            : String(children ?? "")
        const label = /^\[\d+\]$/.test(childText.trim()) ? childText.trim() : `[${childText}]`
        return (
            <a
                href={href}
                className="report-citation-link"
                onClick={(e) => {
                    e.preventDefault()
                    scrollToReportReference(refId)
                }}
                {...props}
            >
                {label}
            </a>
        )
    },
}

function stripReferenceLabelFromCitations(text) {
    if (!text) return text
    return text
        .replace(/\bReferences?\s+(\[\d+\])/gi, "$1")
        .replace(/\bReferences?\s+(\[\d+\]\(#paper-\d+\))/gi, "$1")
        .replace(/\(\s*References?\s+(\[\d+\])\s*\)/gi, "($1)")
        .replace(/\(\s*References?\s+(\[\d+\]\(#paper-\d+\))\s*\)/gi, "($1)")
}

function normalizeReportCitations(text, articles) {
    if (!text || !Array.isArray(articles) || articles.length === 0) return stripReferenceLabelFromCitations(text)
    let result = text
    articles.forEach((article, index) => {
        const id = String(article?.uniqueId ?? article?.pubmed_id ?? "")
        if (!id) return
        const refNum = index + 1
        const escapedId = id.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
        result = result.replace(new RegExp(`Article\\s*\\[${escapedId}\\]`, "gi"), `[${refNum}]`)
        result = result.replace(new RegExp(`\\[${escapedId}\\]`, "g"), `[${refNum}]`)
    })
    return stripReferenceLabelFromCitations(result)
}

function linkifyReportCitations(text, articleCount) {
    if (!text || !articleCount) return text
    return text.replace(/\[(\d+)\](?!\()/g, (match, num) => {
        const n = parseInt(num, 10)
        if (n >= 1 && n <= articleCount) {
            return `[${n}](#paper-${n})`
        }
        return match
    })
}

function prepareReportMarkdown(text, articles) {
    const normalized = normalizeReportCitations(text, articles)
    return linkifyReportCitations(normalized, articles?.length ?? 0)
}

function buildResultsSectionLabels(studyCount, columnLabels) {
    const studyWord = studyCount === 1 ? "study" : "studies"
    if (!columnLabels || columnLabels.length === 0) {
        return {
            sectionLabel: "Results",
            heading: "Characteristics of Included Studies",
            summary: `This review included ${studyCount} ${studyWord}.`,
            studyHeader: "Study",
        }
    }
    const formatted = columnLabels.length === 1
        ? columnLabels[0]
        : `${columnLabels.slice(0, -1).join(", ")} and ${columnLabels[columnLabels.length - 1]}`
    return {
        sectionLabel: "Results",
        heading: "Characteristics of Included Studies",
        summary: `This review included ${studyCount} ${studyWord} with data extracted on ${formatted}.`,
        studyHeader: "Study",
    }
}

function formatCellContent(articleId, columnId, extractionArticleValues) {
    const articleData = extractionArticleValues[articleId] ?? extractionArticleValues[String(articleId)] ?? {}
    const columnData = articleData[columnId]
    if (!columnData || typeof columnData !== "object" || Array.isArray(columnData)) {
        return typeof columnData === "string" ? columnData : "—"
    }
    const values = Object.values(columnData).filter((v) => v && v !== "—" && v !== "null" && String(v).trim() !== "")
    return values.length > 0 ? values.join("\n") : "—"
}

const SortIcon = () => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" className="report-th-sort-icon">
        <path d="M4 5.5L7 2.5L10 5.5" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M4 8.5L7 11.5L10 8.5" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)

const FilterIcon = () => (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg" className="report-th-filter-icon">
        <path d="M1.5 3h10M3.5 6.5h6M5.5 10h2" stroke="#9ca3af" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
)

const ReportGeneration = () => {
    const { id, workflowData, setWorkflowData, navigateToStep, getStepsForPage, handleNavigateToPaperSources, isAdvanced } = useLiteratureReview()
    const reportNav = useLiteratureReviewReport()
    const { setRefreshTrigger } = useContext(NavigationContext)

    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState("")
    const [question, setQuestion] = useState("")
    const [result, setResult] = useState("")
    const [selectedReportType, setSelectedReportType] = useState("original")
    const [simplifiedResult, setSimplifiedResult] = useState(null)
    const [simplifiedSynthesisSection, setSimplifiedSynthesisSection] = useState(null)
    const [isLoadingSimplification, setIsLoadingSimplification] = useState(false)
    const [selectedLanguage, setSelectedLanguage] = useState(null)
    const [isLoadingTranslation, setIsLoadingTranslation] = useState(false)
    const [translatedResult, setTranslatedResult] = useState(null)
    const [translatedSynthSection, setTranslatedSynthSection] = useState(null)
    const [translatedSimplifiedResult, setTranslatedSimplifiedResult] = useState(null)
    const [translatedSimplifiedSynthSection, setTranslatedSimplifiedSynthSection] = useState(null)
    const [translatedColumns, setTranslatedColumns] = useState(null)
    const [translatedCellContent, setTranslatedCellContent] = useState(null)
    const [translatedMethods, setTranslatedMethods] = useState(null)
    const [translatedResultsLabels, setTranslatedResultsLabels] = useState(null)
    const [importantColumnIds, setImportantColumnIds] = useState(null)
    const [synthesisSection, setSynthesisSection] = useState("")
    const [cellSummaries, setCellSummaries] = useState({})
    const [totalAbstracts, setTotalAbstracts] = useState(null)
    const [qualifiedAbstracts, setQualifiedAbstracts] = useState(null)
    const [isFullTextEvaluated, setIsFullTextEvaluated] = useState(false)
    const [fetchedScreenedArticles, setFetchedScreenedArticles] = useState(null)
    const [fetchedSuggestedColumns, setFetchedSuggestedColumns] = useState(null)
    const [fetchedExtractionColumns, setFetchedExtractionColumns] = useState(null)
    const [fetchedExtractionArticleValues, setFetchedExtractionArticleValues] = useState(null)
    const [fetchedArticleUniqueIds, setFetchedArticleUniqueIds] = useState(null)
    const [fetchedFullTextPassedIds, setFetchedFullTextPassedIds] = useState(null)
    const [followUpQuestions, setFollowUpQuestions] = useState([])
    const [researchGap, setResearchGap] = useState(null)
    const [isResearchGapLoading, setIsResearchGapLoading] = useState(false)
    const [researchGapError, setResearchGapError] = useState("")
    const [hasResearchGapStarted, setHasResearchGapStarted] = useState(false)
    const [translatedResearchGap, setTranslatedResearchGap] = useState(null)
    const [simplifiedResearchGap, setSimplifiedResearchGap] = useState(null)
    const [translatedSimplifiedResearchGap, setTranslatedSimplifiedResearchGap] = useState(null)
    const [isStudyTableCollapsed, setIsStudyTableCollapsed] = useState(true)
    const { startPolling: startResearchGapPolling } = usePoll(2000)
    const { startPolling: startSynthesisPolling, stopPolling: stopSynthesisPolling } = usePoll(2000)

    const printRef = useRef(null)
    const handlePrint = useReactToPrint({
        contentRef: printRef,
        documentTitle: `MedRPT-Report-${workflowData.litreviewtitle || question || "Report"}`,
        pageStyle: `
            @page {
                margin: 24mm 20mm;
                @bottom-center {
                    content: counter(page);
                    font-size: 10pt;
                    color: #6b7280;
                    font-family: system-ui, -apple-system, sans-serif;
                }
            }
        `,
    })

    const fullTextPassedIds = workflowData.fullTextArticleIds ?? fetchedFullTextPassedIds ?? null
    const fullTextPassedCount = Array.isArray(fullTextPassedIds) ? fullTextPassedIds.length : null

    const screenedArticles = useMemo(
        () => (fetchedScreenedArticles?.length ? fetchedScreenedArticles : (workflowData.screenedArticles ?? [])),
        [workflowData.screenedArticles, fetchedScreenedArticles]
    )
    const articleUniqueIds = Array.isArray(workflowData.articleUniqueIds) && workflowData.articleUniqueIds.length
        ? workflowData.articleUniqueIds
        : Array.isArray(fetchedArticleUniqueIds)
            ? fetchedArticleUniqueIds
            : []
    const suggestedColumns = Array.isArray(fetchedSuggestedColumns) && fetchedSuggestedColumns.length > 0
        ? fetchedSuggestedColumns
        : workflowData.suggestedColumns ?? []
    const extractionColumns = Array.isArray(workflowData.extractionColumns) && workflowData.extractionColumns.length
        ? workflowData.extractionColumns
        : Array.isArray(fetchedExtractionColumns)
            ? fetchedExtractionColumns
            : []
    const extractionArticleValues = (workflowData.extractionArticleValues && typeof workflowData.extractionArticleValues === "object" && Object.keys(workflowData.extractionArticleValues).length)
        ? workflowData.extractionArticleValues
        : (fetchedExtractionArticleValues && typeof fetchedExtractionArticleValues === "object")
            ? fetchedExtractionArticleValues
            : {}

    const steps = useMemo(() => getStepsForPage("research-report"), [getStepsForPage])

    const hash = (typeof window !== "undefined" && window.location.hash) || ""
    const isReportGenerationPage = hash.replace(/^#/, "").trim() === "research-report"
    console.log("suggestedColumns", suggestedColumns)

    useEffect(() => {
        const applyReportData = (data) => {
            setQuestion(data?.question ?? "")
            setResult(data?.synthesisResult ?? data?.report ?? "")
            if (Array.isArray(data?.importantColumns) && data.importantColumns.length > 0) {
                setImportantColumnIds(data.importantColumns)
            }
            if (data?.synthesisSection) {
                setSynthesisSection(data.synthesisSection)
            }
            if (data?.extractionCellSummaries && typeof data.extractionCellSummaries === "object") {
                setCellSummaries(data.extractionCellSummaries)
            }
            if (data?.totalAbstracts != null) setTotalAbstracts(data.totalAbstracts)
            if (data?.qualifiedAbstracts != null) setQualifiedAbstracts(data.qualifiedAbstracts)
            if (data?.fullTextEvaluated) setIsFullTextEvaluated(true)
            if (Array.isArray(data?.screenedArticles)) setFetchedScreenedArticles(data.screenedArticles)
            if (Array.isArray(data?.abstractSuggestedColumns)) setFetchedSuggestedColumns(data.abstractSuggestedColumns)
            if (Array.isArray(data?.extractionColumns)) setFetchedExtractionColumns(data.extractionColumns)
            if (data?.extractionArticleValues && typeof data.extractionArticleValues === "object") {
                setFetchedExtractionArticleValues(data.extractionArticleValues)
            }
            if (Array.isArray(data?.articleUniqueIds)) setFetchedArticleUniqueIds(data.articleUniqueIds)
            if (Array.isArray(data?.fullTextPassedIds)) setFetchedFullTextPassedIds(data.fullTextPassedIds)
            if (Array.isArray(data?.followUpQuestions)) setFollowUpQuestions(data.followUpQuestions)
            const workflowPatch = {}
            if (data?.question) workflowPatch.question = data.question
            if (Array.isArray(data?.screenedArticles)) workflowPatch.screenedArticles = data.screenedArticles
            if (Array.isArray(data?.articleUniqueIds)) workflowPatch.articleUniqueIds = data.articleUniqueIds
            if (Array.isArray(data?.abstractSuggestedColumns)) workflowPatch.suggestedColumns = data.abstractSuggestedColumns
            if (Array.isArray(data?.extractionColumns)) workflowPatch.extractionColumns = data.extractionColumns
            if (data?.extractionArticleValues && typeof data.extractionArticleValues === "object") {
                workflowPatch.extractionArticleValues = data.extractionArticleValues
            }
            if (Array.isArray(data?.fullTextPassedIds)) workflowPatch.fullTextArticleIds = data.fullTextPassedIds
            if (Object.keys(workflowPatch).length) setWorkflowData(workflowPatch)
            const synthesis = data?.synthesisResult ?? data?.report ?? ""
            if (synthesis) {
                setRefreshTrigger((prev) => !prev)
            }
            if (reportNav?.refreshReportAvailability && synthesis) {
                reportNav.refreshReportAvailability()
            }
        }

        const pollUntilComplete = () => {
            const poll = async (stop) => {
                try {
                    const status = await getSynthesisStatus(id)
                    if (status?.status === "completed") {
                        applyReportData(status)
                        setIsLoading(false)
                        stop()
                    } else if (status?.status === "failed") {
                        setError(status.error || "Failed to generate report")
                        setIsLoading(false)
                        stop()
                    }
                } catch (err) {
                    // Keep polling through transient proxy errors; the background job may still finish.
                    console.error("Error polling report status", err)
                }
            }
            poll(stopSynthesisPolling)
            startSynthesisPolling(poll)
        }

        const fetchReport = async () => {
            if (!id || !isReportGenerationPage) return
            setIsLoading(true)
            setError("")
            try {
                const data = await generateReport(id)
                if (data?.status === "processing") {
                    pollUntilComplete()
                } else {
                    applyReportData(data)
                    setIsLoading(false)
                }
            } catch (err) {
                setError(err?.message || "Failed to generate report")
                setIsLoading(false)
            }

            // Status-only check: shows a previously generated Research Gap analysis
            // on reload without ever kicking off generation on its own.
            try {
                const status = await getResearchGapStatus(id)
                if (status?.status === "completed" && status.researchGap) {
                    setResearchGap(status.researchGap)
                    setHasResearchGapStarted(true)
                }
            } catch {
                // Ignore — the section simply stays in its "not generated" state.
            }
        }
        fetchReport()

        return () => stopSynthesisPolling()
    }, [id, isReportGenerationPage, reportNav?.refreshReportAvailability, setRefreshTrigger, setWorkflowData, startSynthesisPolling, stopSynthesisPolling])

    // If Plain Language and/or a non-English language is already selected when a fresh
    // (English, technical) research gap analysis finishes generating, backfill the
    // simplified/translated versions too, so the section doesn't show English/technical
    // text under a simplified or translated report. Both endpoints only (re)generate
    // whatever's missing from cache, so this stays cheap once the rest of the report
    // has already been simplified/translated. Simplify first, then translate, so the
    // translate call can see the simplified content if it needs to translate that too.
    const refreshResearchGapTranslationIfNeeded = async () => {
        try {
            if (selectedReportType === "plainLanguage") {
                const simplified = await simplifyLitReviewReport(id)
                if (simplified?.simplifiedResearchGap) setSimplifiedResearchGap(simplified.simplifiedResearchGap)
            }
            if (selectedLanguage && selectedLanguage.toLowerCase() !== "english") {
                const translated = await translateLitReviewReport(id, selectedLanguage)
                if (translated?.translatedResearchGap) setTranslatedResearchGap(translated.translatedResearchGap)
                if (translated?.translatedSimplifiedResearchGap) setTranslatedSimplifiedResearchGap(translated.translatedSimplifiedResearchGap)
            }
        } catch (err) {
            console.error("Error refreshing research gap translation/simplification", err)
        }
    }

    const handleGenerateResearchGap = async () => {
        if (!id || isResearchGapLoading) return
        setHasResearchGapStarted(true)
        setResearchGap(null)
        setIsResearchGapLoading(true)
        setResearchGapError("")
        try {
            const data = await generateResearchGap(id)
            if (data?.status === "completed") {
                setResearchGap(data.researchGap)
                setIsResearchGapLoading(false)
                refreshResearchGapTranslationIfNeeded()
                return
            }
            if (data?.status === "processing") {
                startResearchGapPolling(async (stop) => {
                    try {
                        const status = await getResearchGapStatus(id)
                        if (status?.status === "completed") {
                            setResearchGap(status.researchGap)
                            setIsResearchGapLoading(false)
                            stop()
                            refreshResearchGapTranslationIfNeeded()
                        } else if (status?.status === "failed") {
                            setResearchGapError(status.error || "Failed to generate research gap analysis")
                            setIsResearchGapLoading(false)
                            stop()
                        }
                    } catch (err) {
                        setResearchGapError(err?.message || "Failed to generate research gap analysis")
                        setIsResearchGapLoading(false)
                        stop()
                    }
                })
            }
        } catch (err) {
            setResearchGapError(err?.message || "Failed to generate research gap analysis")
            setIsResearchGapLoading(false)
        }
    }

    const handleReportTypeChange = async (reportType) => {
        setSelectedReportType(reportType)
        if (reportType === "plainLanguage" && !simplifiedResult) {
            try {
                setIsLoadingSimplification(true)
                const data = await simplifyLitReviewReport(id)
                if (data.simplifiedSynthesis) setSimplifiedResult(data.simplifiedSynthesis)
                if (data.simplifiedSection) setSimplifiedSynthesisSection(data.simplifiedSection)
                if (data.simplifiedResearchGap) setSimplifiedResearchGap(data.simplifiedResearchGap)
            } catch (err) {
                console.error("Error simplifying report", err)
                setSelectedReportType("original")
            } finally {
                setIsLoadingSimplification(false)
            }
        }
    }

    const handleLanguageChange = async (language) => {
        if (language === selectedLanguage) return
        setSelectedLanguage(language)
        if (!language || language.toLowerCase() === "english") {
            setTranslatedResult(null)
            setTranslatedSynthSection(null)
            setTranslatedSimplifiedResult(null)
            setTranslatedSimplifiedSynthSection(null)
            setTranslatedColumns(null)
            setTranslatedCellContent(null)
            setTranslatedMethods(null)
            setTranslatedResultsLabels(null)
            setTranslatedResearchGap(null)
            setTranslatedSimplifiedResearchGap(null)
            return
        }
        try {
            setIsLoadingTranslation(true)
            const data = await translateLitReviewReport(id, language)
            if (data.translatedSynthesis) setTranslatedResult(data.translatedSynthesis)
            if (data.translatedSection) setTranslatedSynthSection(data.translatedSection)
            if (data.translatedSimplifiedSynthesis) setTranslatedSimplifiedResult(data.translatedSimplifiedSynthesis)
            if (data.translatedSimplifiedSection) setTranslatedSimplifiedSynthSection(data.translatedSimplifiedSection)
            if (data.translatedColumns) setTranslatedColumns(data.translatedColumns)
            if (data.translatedCellContent) setTranslatedCellContent(data.translatedCellContent)
            if (data.translatedMethods) setTranslatedMethods(data.translatedMethods)
            if (data.translatedResultsLabels) setTranslatedResultsLabels(data.translatedResultsLabels)
            if (data.translatedResearchGap) setTranslatedResearchGap(data.translatedResearchGap)
            if (data.translatedSimplifiedResearchGap) setTranslatedSimplifiedResearchGap(data.translatedSimplifiedResearchGap)
        } catch (err) {
            console.error("Error translating report", err)
            setSelectedLanguage(null)
        } finally {
            setIsLoadingTranslation(false)
        }
    }

    const handleBack = () => {
        navigateToStep("data-extraction-results")
    }

    const isNonEnglish = selectedLanguage && selectedLanguage.toLowerCase() !== "english"
    const isPlain = selectedReportType === "plainLanguage"
    const activeResearchGap = isNonEnglish
        ? (isPlain && translatedSimplifiedResearchGap ? translatedSimplifiedResearchGap : (translatedResearchGap || researchGap))
        : (isPlain && simplifiedResearchGap ? simplifiedResearchGap : researchGap)

    const activeTableColumns = useMemo(() => {
        const cols = isNonEnglish && translatedColumns ? translatedColumns : extractionColumns
        return importantColumnIds ? cols.filter((col) => importantColumnIds.includes(col.id)) : cols
    }, [isNonEnglish, translatedColumns, extractionColumns, importantColumnIds])

    const getActiveCell = (articleId, colId) => {
        if (isNonEnglish && translatedCellContent) {
            return translatedCellContent[String(articleId)]?.[colId] ?? "—"
        }
        return cellSummaries[String(articleId)]?.[colId] || formatCellContent(String(articleId), colId, extractionArticleValues)
    }

    const showCharacteristicsTable = activeTableColumns.length > 0 && screenedArticles.length > 0

    const englishResultsLabels = useMemo(
        () => buildResultsSectionLabels(
            screenedArticles.length,
            activeTableColumns.map((col) => col.label ?? col.id)
        ),
        [screenedArticles.length, activeTableColumns]
    )
    const resultsLabels = isNonEnglish && translatedResultsLabels
        ? translatedResultsLabels
        : englishResultsLabels

    const normalizedResult = useMemo(
        () => prepareReportMarkdown(result, screenedArticles),
        [result, screenedArticles]
    )
    const normalizedSynthesisSection = useMemo(
        () => prepareReportMarkdown(synthesisSection, screenedArticles),
        [synthesisSection, screenedArticles]
    )
    const normalizedSimplifiedResult = useMemo(
        () => prepareReportMarkdown(simplifiedResult, screenedArticles),
        [simplifiedResult, screenedArticles]
    )
    const normalizedSimplifiedSynthesisSection = useMemo(
        () => prepareReportMarkdown(simplifiedSynthesisSection, screenedArticles),
        [simplifiedSynthesisSection, screenedArticles]
    )
    const normalizedTranslatedResult = useMemo(
        () => prepareReportMarkdown(translatedResult, screenedArticles),
        [translatedResult, screenedArticles]
    )
    const normalizedTranslatedSynthSection = useMemo(
        () => prepareReportMarkdown(translatedSynthSection, screenedArticles),
        [translatedSynthSection, screenedArticles]
    )
    const normalizedTranslatedSimplifiedResult = useMemo(
        () => prepareReportMarkdown(translatedSimplifiedResult, screenedArticles),
        [translatedSimplifiedResult, screenedArticles]
    )
    const normalizedTranslatedSimplifiedSynthSection = useMemo(
        () => prepareReportMarkdown(translatedSimplifiedSynthSection, screenedArticles),
        [translatedSimplifiedSynthSection, screenedArticles]
    )
    const normalizedTranslatedMethods = useMemo(
        () => prepareReportMarkdown(translatedMethods, screenedArticles),
        [translatedMethods, screenedArticles]
    )

    const activeReportMarkdown = isNonEnglish
        ? (isPlain && normalizedTranslatedSimplifiedResult
            ? normalizedTranslatedSimplifiedResult
            : (normalizedTranslatedResult || normalizedResult))
        : (isPlain && normalizedSimplifiedResult ? normalizedSimplifiedResult : normalizedResult)

    const activeSynthesisMarkdown = isNonEnglish
        ? (isPlain && normalizedTranslatedSimplifiedSynthSection
            ? normalizedTranslatedSimplifiedSynthSection
            : (normalizedTranslatedSynthSection || normalizedSynthesisSection))
        : (isPlain && normalizedSimplifiedSynthesisSection ? normalizedSimplifiedSynthesisSection : normalizedSynthesisSection)

    const handleExportPpt = async () => {
        try {
            const tableHeaders = showCharacteristicsTable
                ? [resultsLabels.studyHeader, ...activeTableColumns.map((col) => col.label ?? col.id)]
                : []
            const tableRows = showCharacteristicsTable
                ? articleUniqueIds.map((articleId) => {
                    const article = screenedArticles.find((s) => String(s?.uniqueId) === String(articleId)) ?? {}
                    return [
                        formatStudyCitation(article),
                        ...activeTableColumns.map((col) => getActiveCell(articleId, col.id)),
                    ]
                })
                : []

            await exportLitReviewPpt({
                title: workflowData.litreviewtitle || question,
                question,
                date: formatReportDate(),
                overviewMarkdown: activeReportMarkdown,
                methods: {
                    translatedMarkdown: isNonEnglish ? normalizedTranslatedMethods : null,
                    question,
                    totalAbstracts,
                    qualifiedAbstracts,
                    suggestedColumns,
                    screenedCount: screenedArticles.length,
                    isFullTextEvaluated,
                    fullTextPassedCount,
                    extractionColumns: isNonEnglish && translatedColumns ? translatedColumns : extractionColumns,
                },
                table: showCharacteristicsTable
                    ? {
                        heading: resultsLabels.heading,
                        summary: resultsLabels.summary,
                        headers: tableHeaders,
                        rows: tableRows,
                    }
                    : null,
                synthesisMarkdown: activeSynthesisMarkdown,
                researchGap: hasResearchGapStarted && activeResearchGap && !researchGapError
                    ? activeResearchGap
                    : null,
                articles: screenedArticles,
                fileName: workflowData.litreviewtitle || question || "Report",
            })
        } catch (err) {
            console.error("Failed to export PowerPoint", err)
        }
    }

    const mainContent = (
        <div className="report-generation-page">
            {isLoading ? (
                <div className="report-generation-loading">
                    <CircularProgress sx={{ color: "#367c7e" }} size={40} />
                    <p className="report-generation-loading-message">Generating report…</p>
                </div>
            ) : isLoadingSimplification ? (
                <div className="report-generation-loading">
                    <CircularProgress sx={{ color: "#367c7e" }} size={40} />
                    <p className="report-generation-loading-message">Simplifying report…</p>
                </div>
            ) : isLoadingTranslation ? (
                <div className="report-generation-loading">
                    <CircularProgress sx={{ color: "#367c7e" }} size={40} />
                    <p className="report-generation-loading-message">Translating report…</p>
                </div>
            ) : error ? (
                <div className="report-text-content">
                    <div className="report-generation-status report-generation-status-error">{error}</div>
                </div>
            ) : (
                <div ref={printRef} className="report-printable-area">
                    <div className="report-text-content">
                        <div className="report-generation-date">{formatReportDate()}</div>
                        {question && <h1 className="report-generation-title">{question}</h1>}
                        {activeReportMarkdown && (
                            <div className="report-generation-result">
                                <ReactMarkdown components={reportMarkdownComponents}>
                                    {activeReportMarkdown}
                                </ReactMarkdown>
                            </div>
                        )}
                    </div>

                    {isNonEnglish && normalizedTranslatedMethods ? (
                        <section className="report-methods-section">
                            <div className="report-text-content">
                                <div className="report-section-label">Methods</div>
                                <ReactMarkdown components={reportMarkdownComponents}>{normalizedTranslatedMethods}</ReactMarkdown>
                            </div>
                        </section>
                    ) : (
                        <>
                            {(question || screenedArticles.length > 0) && (
                                <section className="report-methods-section">
                                    <div className="report-text-content">
                                        <div className="report-section-label">Methods</div>
                                        <h2 className="report-methods-heading">Paper Search</h2>
                                        {question && (
                                            <p className="report-methods-paragraph">
                                                We ran the following query: &#8220;{question}&#8221;
                                            </p>
                                        )}
                                        {totalAbstracts != null && (
                                            <p className="report-methods-paragraph">
                                                The search returned {totalAbstracts} total results.
                                            </p>
                                        )}
                                        {qualifiedAbstracts != null && (
                                            <p className="report-methods-paragraph">
                                                We retrieved {qualifiedAbstracts} papers most relevant to the query for screening.
                                            </p>
                                        )}
                                    </div>
                                </section>
                            )}

                            {suggestedColumns.length > 0 && (
                                <section className="report-methods-section">
                                    <div className="report-text-content">
                                        <h2 className="report-methods-heading">Screening</h2>
                                        <h2 className="report-methods-subheading">Abstract screening</h2>
                                        <p className="report-methods-paragraph">
                                            We screened in sources based on their abstracts that met these criteria:
                                        </p>
                                        <ul className="report-screening-criteria-list">
                                            {suggestedColumns.map((col) => (
                                                <li key={col.id} className="report-screening-criteria-item">
                                                    <strong>{col.label ?? col.id}</strong>
                                                    {col.screeningQuestion ? `: ${col.screeningQuestion}` : ""}
                                                </li>
                                            ))}
                                        </ul>
                                        {screenedArticles.length > 0 && (
                                            <p className="report-methods-paragraph">
                                                {screenedArticles.length} paper{screenedArticles.length !== 1 ? "s" : ""} passed abstract screening. Papers below the screening threshold score were excluded.
                                            </p>
                                        )}
                                    </div>
                                </section>
                            )}

                            {isFullTextEvaluated && suggestedColumns.length > 0 && (
                                <section className="report-methods-section">
                                    <div className="report-text-content">
                                        <h2 className="report-methods-subheading">Full-text screening</h2>
                                        <p className="report-methods-paragraph">
                                            We then screened papers based on their full texts that met these criteria:
                                        </p>
                                        <ul className="report-screening-criteria-list">
                                            {suggestedColumns.map((col) => (
                                                <li key={col.id} className="report-screening-criteria-item">
                                                    <strong>{col.label ?? col.id}</strong>
                                                    {col.screeningQuestion ? `: ${col.screeningQuestion}` : ""}
                                                </li>
                                            ))}
                                        </ul>
                                        {fullTextPassedCount != null && (
                                            <p className="report-methods-paragraph">
                                                {fullTextPassedCount} paper{fullTextPassedCount !== 1 ? "s" : ""} passed full-text screening. Papers below the screening threshold score were excluded.
                                            </p>
                                        )}
                                    </div>
                                </section>
                            )}

                            {extractionColumns.length > 0 && (
                                <section className="report-methods-section">
                                    <div className="report-text-content">
                                        <h2 className="report-methods-heading">Data Extraction</h2>
                                        <p className="report-methods-paragraph">
                                            We asked a large language model to extract the following data columns from each paper, using the detailed instructions provided for each column.
                                        </p>
                                        <ul className="report-screening-criteria-list">
                                            {extractionColumns.map((col) => (
                                                <li key={col.id} className="report-screening-criteria-item">
                                                    <strong>{col.label ?? col.id}</strong>:
                                                    {Array.isArray(col.fields) && col.fields.length > 0 && (
                                                        <ul className="report-extraction-fields-list">
                                                            {col.fields.map((field, idx) => (
                                                                <li key={field.name ?? idx} className="report-extraction-field-item">
                                                                    {field.label ?? field.description ?? field.name}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </section>
                            )}
                        </>
                    )}

                    {showCharacteristicsTable && (
                        <section className="report-characteristics-section">
                            <div className="report-text-content">
                                <div className="report-section-label">{resultsLabels.sectionLabel}</div>
                                <h2 className="report-characteristics-heading">{resultsLabels.heading}</h2>
                                <p className="report-characteristics-summary">
                                    {resultsLabels.summary}
                                </p>
                            </div>

                            <div className="report-characteristics-table-wrapper">
                                <table className="report-characteristics-table">
                                    <thead>
                                        <tr>
                                            <th className="report-characteristics-col-study">
                                                <div className="report-th-content">
                                                    <span>{resultsLabels.studyHeader}</span>
                                                </div>
                                            </th>
                                            {activeTableColumns.map((col) => (
                                                <th key={col.id}>
                                                    <div className="report-th-content">
                                                        <span>{col.label ?? col.id}</span>
                                                    </div>
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {articleUniqueIds.map((articleId, rowIndex) => {
                                            const article = screenedArticles.find((s) => String(s?.uniqueId) === String(articleId)) ?? {}
                                            const citation = formatStudyCitation(article)
                                            const isHiddenRow = isStudyTableCollapsed && rowIndex >= STUDY_ROWS_PREVIEW_COUNT
                                            return (
                                                <tr
                                                    key={String(articleId)}
                                                    className={isHiddenRow ? "report-characteristics-row-collapsed" : undefined}
                                                >
                                                    <td className="report-characteristics-cell-study">
                                                        {citation}
                                                    </td>
                                                    {activeTableColumns.map((col) => (
                                                        <td key={col.id} className="report-characteristics-cell">
                                                            {getActiveCell(articleId, col.id)}
                                                        </td>
                                                    ))}
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            {articleUniqueIds.length > STUDY_ROWS_PREVIEW_COUNT && (
                                <button
                                    type="button"
                                    className="report-characteristics-view-more-button"
                                    onClick={() => setIsStudyTableCollapsed((prev) => !prev)}
                                >
                                    {isStudyTableCollapsed
                                        ? `View More (${articleUniqueIds.length - STUDY_ROWS_PREVIEW_COUNT} more)`
                                        : "View Less"}
                                </button>
                            )}
                        </section>
                    )}

                    {activeSynthesisMarkdown && (
                        <section className="report-synthesis-section">
                            <div className="report-text-content">
                                <div className="report-section-label">Analysis</div>
                                <h2 className="report-synthesis-heading">Synthesis</h2>
                                <div className="report-synthesis-content">
                                    <ReactMarkdown components={reportMarkdownComponents}>
                                        {activeSynthesisMarkdown}
                                    </ReactMarkdown>
                                </div>
                            </div>
                        </section>
                    )}

                    {Object.keys(extractionArticleValues).length > 0 && (
                        <div className="report-text-content">
                            <ResearchGap
                                researchGap={activeResearchGap}
                                isLoading={isResearchGapLoading}
                                error={researchGapError}
                                hasGenerated={hasResearchGapStarted}
                                onGenerate={handleGenerateResearchGap}
                                articles={screenedArticles}
                            />
                        </div>
                    )}

                    {followUpQuestions.length > 0 && (
                        <div className="report-text-content">
                            <FollowUpQuestions
                                questions={followUpQuestions}
                                onQuestionClick={handleNavigateToPaperSources}
                            />
                        </div>
                    )}

                    {screenedArticles.length > 0 && (
                        <div className="report-text-content">
                            <section className="report-generation-references">
                                <h2 className="report-generation-references-title">References</h2>
                                <ol className="report-generation-references-list">
                                    {screenedArticles.map((article, index) => {
                                        const title = article?.title ?? ""
                                        const rawAuthors = article?.authors
                                        const authors = Array.isArray(rawAuthors) ? rawAuthors.join(", ") : (rawAuthors ?? "")
                                        const journal = article?.journalTitle ?? ""
                                        const publicationDate = article?.publicationDate ?? ""
                                        const citations = article?.citationCount ?? 0
                                        const pmid = article?.uniqueId ?? article?.pubmed_id ?? articleUniqueIds[index]
                                        const linkUrl = pmid ? `https://pubmed.ncbi.nlm.nih.gov/${pmid}` : null
                                        return (
                                            <li
                                                key={(article?.uniqueId ?? article?.pubmed_id ?? index) || index}
                                                id={`paper-${index + 1}`}
                                                className="report-generation-ref-item"
                                            >
                                                <div className="report-generation-ref-title">{title}</div>
                                                {(authors || journal || publicationDate || citations > 0) && (
                                                    <div className="report-generation-ref-meta">
                                                        {authors && <span className="report-generation-ref-authors">{authors}</span>}
                                                        {journal && <span className="report-generation-ref-journal">{journal}</span>}
                                                        {publicationDate && <span className="report-generation-ref-date">{publicationDate}</span>}
                                                        {citations > 0 && <span className="report-generation-ref-citations">{citations} citations</span>}
                                                    </div>
                                                )}
                                                {linkUrl && (
                                                    <a
                                                        href={linkUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="report-generation-ref-link"
                                                    >
                                                        View full study
                                                    </a>
                                                )}
                                            </li>
                                        )
                                    })}
                                </ol>
                            </section>
                        </div>
                    )}
                </div>
            )}
        </div>
    )

    const savePdfButton = !isLoading && !error ? (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <ReportTypeSelector
                selectedReportType={selectedReportType}
                onReportTypeChange={handleReportTypeChange}
            />
            <LanguageSelector
                selectedLanguage={selectedLanguage}
                onLanguageChange={handleLanguageChange}
            />
            <button className="report-save-pdf-button" onClick={handlePrint}>
                Save as PDF
            </button>
            {!isAdvanced && (
                <button className="report-save-pdf-button" onClick={handleExportPpt}>
                    Save as PPT
                </button>
            )}
        </div>
    ) : null

    return (
        <LiteratureReviewLayout
            projectTitle={workflowData.litreviewtitle || "Report generation"}
            editableTitle={!!id}
            steps={steps}
            mainContent={mainContent}
            headerActions={savePdfButton}
            onBack={handleBack}
            backLabel="Back to extraction results"
            showBack={true}
            showNext={false}
        />
    )
}

export default ReportGeneration
